import type { BulletSystem } from './bullets';

/**
 * Boss fields that act on the PLAYER's shots (docs/02 B1 defences): Tide's
 * deflecting wave rings, Magnet's poles, Gale's storm wall, Chronos stasis,
 * Singularity's accretion orbit.
 * They steer live pooled bullets in place — no allocation.
 */

/**
 * Reflect friendly bullets that meet an expanding ring (radius r ± band)
 * while flying INWARD — they bounce back out. @returns bullets deflected
 */
export function deflectRing(
  bullets: BulletSystem, cx: number, cy: number, r: number, band: number,
): number {
  let n = 0;
  for (const b of bullets.liveList) {
    if (!b.friendly) continue;
    const dx = b.x - cx;
    const dy = b.y - cy;
    const d = Math.hypot(dx, dy);
    if (d < 1 || d < r - band || d > r + band) continue;
    const nx = dx / d;
    const ny = dy / d;
    const vr = b.vx * nx + b.vy * ny;
    if (vr >= 0) continue; // already heading out
    b.vx -= 2 * vr * nx;
    b.vy -= 2 * vr * ny;
    n++;
  }
  return n;
}

/**
 * Bend friendly bullets inside an annulus: `push` (px/s², + outward / −
 * inward) and `swirl` (rad/s turn). Speed is kept — the path bends, the
 * shot does not slow down.
 */
export function bendFriendly(
  bullets: BulletSystem, cx: number, cy: number, rIn: number, rOut: number,
  push: number, swirl: number, dt: number,
): void {
  const c = Math.cos(swirl * dt);
  const s = Math.sin(swirl * dt);
  for (const b of bullets.liveList) {
    if (!b.friendly) continue;
    const dx = b.x - cx;
    const dy = b.y - cy;
    const d = Math.hypot(dx, dy);
    if (d < rIn || d > rOut || d < 1) continue;
    const speed = Math.hypot(b.vx, b.vy);
    let vx = b.vx + (dx / d) * push * dt;
    let vy = b.vy + (dy / d) * push * dt;
    const rx = vx * c - vy * s;
    vy = vx * s + vy * c;
    vx = rx;
    const k = speed / (Math.hypot(vx, vy) || 1);
    b.vx = vx * k;
    b.vy = vy * k;
  }
}

/** Friendly bullets inside radius r crawl at `scale` speed for one step. */
export function stasisFriendly(
  bullets: BulletSystem, cx: number, cy: number, r: number, scale: number,
): void {
  for (const b of bullets.liveList) {
    if (!b.friendly) continue;
    if (Math.hypot(b.x - cx, b.y - cy) < r) b.slow = scale;
  }
}

/**
 * Friendly bullets inside radius r fall into ORBIT: they whirl around the
 * centre at `speed` (spin +1 = clockwise on screen) while sinking inward
 * at `sink` px/s, until the core swallows them or they expire.
 */
export function orbitFriendly(
  bullets: BulletSystem, cx: number, cy: number, r: number,
  speed: number, sink: number, spin: number,
): void {
  for (const b of bullets.liveList) {
    if (!b.friendly) continue;
    const dx = b.x - cx;
    const dy = b.y - cy;
    const d = Math.hypot(dx, dy);
    if (d > r || d < 1) continue;
    const nx = dx / d;
    const ny = dy / d;
    b.vx = -ny * spin * speed - nx * sink;
    b.vy = nx * spin * speed - ny * sink;
  }
}
